/**
 * Raid threat field SSOT — SoftThreat (body/melee/pack) + DangerZone bridge.
 * DangerZone geometry/query lives in raid-danger-zone.ts; this module owns
 * Soft volumes, LHF, space/kite goals, and re-exports DZ for call-site stability.
 */

import { raidCatalog } from "../../data/raid-catalog";
import { sampleThreatAt, type ThreatVolume } from "../../sim";
import type { RaidBoss, RaidDanger, RaidUnit } from "./raid-types";
import threatVolumeJson from "../../data/tables/raid/threat-volume.json";
import { aaClearPadPxOf } from "./raid-disposition";
import {
  type RaidDangerZone,
  clearOfPackAaZones,
  collectPackAaZones,
  collectRaidDangerZones,
} from "./raid-danger-zone";
import {
  RaidEnemyTag,
  enemyHasTag,
  enemyIsCombatRelevant,
  enemyMeleeRadiusPad,
  enemySoftWeightKey,
} from "./raid-enemy";

export type { CollectDangerOpts, RaidDangerZone } from "./raid-danger-zone";
export {
  collectRaidDangerZones,
  collectPackAaZones,
  clearOfPackAaZones,
  isSkillDangerZone,
  isPackAaDangerZone,
  pointInDangerZone,
  pointInAnyDangerZone,
  unitInDangerZones,
  pointClearsDangerZone,
  pointClearsAllDangerZones,
  projectClearOfDangerZones,
  firstEnteredDangerZoneOnSegment,
  segmentEntersDangerZone,
  activeDangerZoneAt,
  hasDangerTier,
} from "./raid-danger-zone";

type ThreatTable = typeof threatVolumeJson;

function threatTable(): ThreatTable {
  return (
    (raidCatalog as { threatVolume?: ThreatTable }).threatVolume ??
    threatVolumeJson
  );
}

/**
 * Authored skill `danger` is a pure number. Telegraph color is threshold-only:
 * `danger >= dangerColorRedAt` → red, else yellow.
 * Red/yellow are NOT "hard vs easy to dodge" — Role rules (tank red-only) and
 * SoftThreat sample weights hang off the derived color.
 */
export function raidDangerColor(danger: number): RaidDanger {
  const thr = Number(threatTable().dangerColorRedAt);
  const at = Number.isFinite(thr) ? thr : 35;
  return danger >= at ? "red" : "yellow";
}

/** SoftThreat sample weight for a derived danger color. */
export function softThreatWeightForDanger(danger: RaidDanger): number {
  const w = threatTable().weights;
  return danger === "red" ? w.red : w.telegraph;
}

function demoMeleeReachPad(boss: RaidBoss): number {
  const row = (
    raidCatalog.bosses as {
      colossus?: { melee?: { reachPad?: number } };
    }
  ).colossus?.melee;
  return Number(row?.reachPad) || 36;
}

export type RaidThreatOpts = {
  /** Living team-1 units (thralls/brutes). Unarmed brutes skipped for soft melee. */
  enemies?: readonly RaidUnit[];
  /**
   * When true, include boss soft body/melee + boss DangerZones.
   * False while boss is dormant / not combat-armed.
   */
  includeBoss?: boolean;
};

function pushDangerZoneVolumes(
  vols: ThreatVolume[],
  zones: readonly RaidDangerZone[],
  _t: ThreatTable,
): void {
  for (const z of zones) {
    const weight = softThreatWeightForDanger(z.danger);
    if (z.shape === "circle") {
      vols.push({
        kind: "circle",
        x: z.x,
        y: z.y,
        radius: z.range,
        weight,
      });
    } else if (z.shape === "line") {
      // Approximate corridor as a narrow cone for segment sampling.
      // Do NOT use coneDeg:0 (charger telegraph) — that collapses to a ray
      // and understates path cost through the charge line.
      const mid = Math.max(40, z.range * 0.5);
      const hw = Math.max(1, z.halfWidth ?? 20);
      const coneDeg = Math.min(
        90,
        Math.max(4, (2 * Math.atan2(hw, mid) * 180) / Math.PI),
      );
      vols.push({
        kind: "cone",
        x: z.x,
        y: z.y,
        range: z.range,
        aimAngle: z.aimAngle ?? 0,
        coneDeg,
        weight,
      });
    } else {
      vols.push({
        kind: "cone",
        x: z.x,
        y: z.y,
        range: z.range,
        aimAngle: z.aimAngle ?? -Math.PI / 2,
        coneDeg: z.coneDeg ?? 60,
        weight,
      });
    }
  }
}

function pushEnemyMeleeVolumes(
  vols: ThreatVolume[],
  enemies: readonly RaidUnit[],
  unitRadius: number,
  t: ThreatTable,
): void {
  const wAdd = t.weights.enemyMelee ?? t.weights.melee;
  const wBrute = t.weights.enemyBruteMelee ?? wAdd;
  for (const e of enemies) {
    if (!enemyIsCombatRelevant(e) || !e.combatArmed) continue;
    // Soft disk alone understates pack AA — sample at least hit reach
    // so sticky melee pressure opens space before feet take free swings.
    const softR = enemyMeleeDiskRadius(e, unitRadius, t);
    const hitR = enemyHitReach(e, unitRadius);
    const key = enemySoftWeightKey(e);
    vols.push({
      kind: "circle",
      x: e.x,
      y: e.y,
      radius: Math.max(softR, hitR),
      weight: key === "enemyBruteMelee" ? wBrute : wAdd,
    });
  }
}

/** Soft body + melee only (no AoE — those come from DangerZones). */
function pushBossSoftVolumes(
  vols: ThreatVolume[],
  boss: RaidBoss,
  time: number,
  unitRadius: number,
  t: ThreatTable,
): void {
  const w = t.weights;
  const broken = time < boss.stunnedUntil;
  if (broken) {
    vols.push({
      kind: "circle",
      x: boss.x,
      y: boss.y,
      radius: boss.radius + t.bodyPad + unitRadius * 0.25,
      weight: w.bodyBroken,
    });
    return;
  }

  vols.push({
    kind: "circle",
    x: boss.x,
    y: boss.y,
    radius: boss.radius + t.bodyPad + unitRadius,
    weight: w.body,
  });

  const meleeR =
    boss.radius + demoMeleeReachPad(boss) + t.meleePad + unitRadius;
  vols.push({
    kind: "circle",
    x: boss.x,
    y: boss.y,
    radius: meleeR,
    weight: w.melee,
  });
}

/**
 * Local Hostile Field (Soft only): enemy melee disks + boss body/melee.
 * DangerZones are NOT included — those feed T0 dodge only.
 */
export function buildLocalHostileVolumes(
  boss: RaidBoss,
  time: number,
  unitRadius = 12,
  opts?: RaidThreatOpts,
): ThreatVolume[] {
  const t = threatTable();
  const vols: ThreatVolume[] = [];
  const enemies = opts?.enemies;
  if (enemies && enemies.length > 0) {
    pushEnemyMeleeVolumes(vols, enemies, unitRadius, t);
  }
  const includeBoss = opts?.includeBoss !== false;
  if (includeBoss) {
    pushBossSoftVolumes(vols, boss, time, unitRadius, t);
  }
  return vols;
}

/**
 * Soft + DangerZones bag (healer path / legacy). Feet & survive use
 * {@link buildLocalHostileVolumes} instead.
 */
export function raidThreatVolumes(
  boss: RaidBoss,
  time: number,
  unitRadius = 12,
  opts?: RaidThreatOpts,
): ThreatVolume[] {
  const t = threatTable();
  const vols = buildLocalHostileVolumes(boss, time, unitRadius, opts);
  const enemies = opts?.enemies;
  const includeBoss = opts?.includeBoss !== false;
  const bossBroken = time < boss.stunnedUntil;
  const zones = collectRaidDangerZones(boss, {
    enemies,
    includeBoss,
    bossBroken: includeBoss && bossBroken,
  });
  pushDangerZoneVolumes(vols, zones, t);
  return vols;
}

export type LocalHostileSnapshot = {
  intensity: number;
  inMelee: boolean;
  packCount: number;
  packPressure: boolean;
};

/** Soft sample + pack/melee booleans at feet. Enemy melee volumes always;
 * boss soft only when `boss` is passed and includeBoss≠false. */
export function localHostileSnapshot(args: {
  x: number;
  y: number;
  unitRadius: number;
  enemies: readonly RaidUnit[];
  boss?: RaidBoss;
  time?: number;
  includeBoss?: boolean;
  vols?: ThreatVolume[];
}): LocalHostileSnapshot {
  let vols = args.vols;
  if (!vols) {
    const t = threatTable();
    vols = [];
    if (args.enemies.length > 0) {
      pushEnemyMeleeVolumes(vols, args.enemies, args.unitRadius, t);
    }
    if (args.boss != null && args.includeBoss !== false) {
      pushBossSoftVolumes(
        vols,
        args.boss,
        args.time ?? 0,
        args.unitRadius,
        t,
      );
    }
  }
  const intensity = sampleThreatAt(args.x, args.y, vols);
  const packCount = countPackHostilesNear(args.x, args.y, args.enemies);
  return {
    intensity,
    inMelee: inEnemyMeleeDisk(
      args.x,
      args.y,
      args.unitRadius,
      args.enemies,
    ),
    packCount,
    packPressure: packCount >= raidPackCountThreshold(),
  };
}

/** Soft body disk radius (armed enemy) for a hero at unitRadius. */
export function enemyMeleeDiskRadius(
  e: Pick<RaidUnit, "radius" | "defId">,
  unitRadius: number,
  t: ThreatTable = threatTable(),
): number {
  const fallback = t.enemyMelee?.radiusPad ?? 4;
  const rPad = enemyMeleeRadiusPad(e) || fallback;
  return e.radius + rPad + unitRadius;
}

function hostileWeight(e: Pick<RaidUnit, "defId">, t: ThreatTable): number {
  const key = enemySoftWeightKey(e);
  return key === "enemyBruteMelee"
    ? (t.weights.enemyBruteMelee ?? t.weights.enemyMelee ?? 14)
    : (t.weights.enemyMelee ?? 14);
}

/** Count combat-armed living hostiles within pack radius of (x,y). */
export function countPackHostilesNear(
  x: number,
  y: number,
  enemies: readonly RaidUnit[],
  packRadius?: number,
): number {
  const t = threatTable();
  const r = packRadius ?? t.pack?.radius ?? 140;
  let n = 0;
  for (const e of enemies) {
    if (!enemyIsCombatRelevant(e) || !e.combatArmed) continue;
    if (
      !enemyHasTag(e, RaidEnemyTag.PackMember) &&
      !enemyHasTag(e, RaidEnemyTag.Elite)
    ) {
      continue;
    }
    const d = Math.hypot(e.x - x, e.y - y);
    if (d <= r + e.radius) n += 1;
  }
  return n;
}

export function raidPackCountThreshold(): number {
  return Number(threatTable().pack?.countThreshold) || 2;
}

export function raidPackRadius(): number {
  return Number(threatTable().pack?.radius) || 140;
}

export function raidPackFleeDist(role?: "tank" | "dps" | "healer"): number {
  const pack = threatTable().pack;
  if (role === "tank") {
    const tank = Number(pack?.tankFleeDist);
    if (Number.isFinite(tank) && tank > 0) return tank;
  }
  if (role === "dps" || role === "healer") {
    const ranged = Number(pack?.rangedFleeDist);
    if (Number.isFinite(ranged) && ranged > 0) return ranged;
  }
  return Number(pack?.fleeDist) || 110;
}

export function raidPackAllyPull(role?: "tank" | "dps" | "healer"): number {
  const pack = threatTable().pack;
  let v: number;
  if (role === "tank") {
    v = Number(pack?.tankAllyPull);
    if (!Number.isFinite(v)) v = Number(pack?.allyPull);
  } else if (role === "dps" || role === "healer") {
    v = Number(pack?.rangedAllyPull);
    if (!Number.isFinite(v)) v = Number(pack?.allyPull);
  } else {
    v = Number(pack?.allyPull);
  }
  return Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : 0.28;
}

function raidPackStandoffPad(role?: "tank" | "dps" | "healer"): number {
  const pack = threatTable().pack;
  if (role === "tank") {
    const tankPad = Number(pack?.tankMinNearestStandoffPad);
    if (Number.isFinite(tankPad) && tankPad >= 0) return tankPad;
  }
  return Number(pack?.minNearestStandoffPad) || 28;
}

function raidPackTankMaxStepPx(): number | null {
  const v = Number(threatTable().pack?.tankMaxStepPx);
  if (!Number.isFinite(v) || v <= 0) return null;
  return v;
}

/** True when feet sit inside any combat-armed enemy melee disk. */
export function inEnemyMeleeDisk(
  x: number,
  y: number,
  unitRadius: number,
  enemies: readonly RaidUnit[],
): boolean {
  const t = threatTable();
  for (const e of enemies) {
    if (e.team !== 1 || e.hp <= 0 || !e.combatArmed) continue;
    const r = enemyMeleeDiskRadius(e, unitRadius, t);
    if (Math.hypot(e.x - x, e.y - y) <= r) return true;
  }
  return false;
}

/**
 * Center→center distance at which an armed enemy can hit this hero
 * (`enemy.attackRange + hero.radius` — same gate as thrall/brute AA).
 */
export function enemyHitReach(
  e: Pick<RaidUnit, "attackRange">,
  unitRadius: number,
): number {
  return e.attackRange + unitRadius;
}

/**
 * True when feet are outside every combat-armed enemy's hit reach (+eps).
 * Kiting plant / Soft exit safety uses this — Soft melee disk alone is not enough.
 */
export function clearOfEnemyAttack(
  x: number,
  y: number,
  unitRadius: number,
  enemies: readonly RaidUnit[],
  eps = 2,
): boolean {
  const clear = Math.max(0, eps);
  for (const e of enemies) {
    if (e.team !== 1 || e.hp <= 0 || !e.combatArmed) continue;
    const reach = enemyHitReach(e, unitRadius) + clear;
    if (Math.hypot(e.x - x, e.y - y) <= reach) return false;
  }
  return true;
}

/**
 * Pack-chew AA clear (`packChew` tag → packAa zones). Charge sidestep must not
 * be cancelled by elite/charger bodies — keep-clear into pack chew is a wipe risk.
 */
export function clearOfPackChewAttack(
  x: number,
  y: number,
  unitRadius: number,
  enemies: readonly RaidUnit[],
  eps = 2,
): boolean {
  return clearOfPackAaZones(
    x,
    y,
    unitRadius,
    collectPackAaZones(enemies),
    eps,
  );
}

/** @deprecated Use {@link clearOfPackChewAttack}. */
export const clearOfThrallAttack = clearOfPackChewAttack;

/** True when feet sit inside any packChew AA circle. */
export function feetInPackChew(
  x: number,
  y: number,
  unitRadius: number,
  enemies: readonly RaidUnit[],
  eps = 2,
): boolean {
  return !clearOfPackChewAttack(x, y, unitRadius, enemies, eps);
}

/**
 * Soft space latch hold: inside Soft melee disk+pad **or** enemy hit reach+pad.
 * Exit only when past both (thrall AA ≫ Soft disk — must not plant in AA).
 */
export function nearEnemyHitOrMelee(
  x: number,
  y: number,
  unitRadius: number,
  enemies: readonly RaidUnit[],
  pad = 0,
): boolean {
  const t = threatTable();
  const clear = Math.max(0, pad);
  for (const e of enemies) {
    if (e.team !== 1 || e.hp <= 0 || !e.combatArmed) continue;
    const melee = enemyMeleeDiskRadius(e, unitRadius, t) + clear;
    const hit = enemyHitReach(e, unitRadius) + clear;
    if (Math.hypot(e.x - x, e.y - y) <= Math.max(melee, hit)) return true;
  }
  return false;
}

/** True when local hostile density exceeds table threshold. */
export function packPressureAbove(
  x: number,
  y: number,
  enemies: readonly RaidUnit[],
): boolean {
  return (
    countPackHostilesNear(x, y, enemies) >= raidPackCountThreshold()
  );
}

export type SpaceFromThreatOpts = {
  fleeDist?: number;
  /**
   * Bias goal toward allyAnchor (0..1). Kit passes follow×table allyPull;
   * tests may pass a literal.
   */
  allyPull?: number;
  role?: "tank" | "dps" | "healer";
  /** Extra px past Soft∪AA for nearest standoff (자기보호). Charge should pass 0. */
  aaClearPad?: number;
};

/** True when any active line charge corridor is painted. */
export function chargeCorridorActive(
  zones: readonly RaidDangerZone[],
): boolean {
  for (const z of zones) {
    if (z.shape === "line" && z.skillId === "charge") return true;
  }
  return false;
}

/**
 * 자기보호 AA margin. While a charge corridor is active, mute strongly
 * (×0.25) so ironhorn sidestep is not cancelled by thrall-AA push geometry,
 * but survivors still keep a thin AA floor during multi-floor charge+brass.
 */
export function effectiveAaClearPadPx(
  u: RaidUnit,
  zones: readonly RaidDangerZone[],
): number {
  const pad = aaClearPadPxOf(u);
  if (chargeCorridorActive(zones)) return pad * 0.25;
  return pad;
}

/**
 * Goal away from local hostile weighted-COM (nearest blend) × fleeDist,
 * then nearest melee standoff. Optional allyPull blends toward allyAnchor
 * without collapsing into pack COM.
 * Tanks keep tankMaxStepPx Role cap.
 */
export function spaceFromThreatGoal(
  u: { x: number; y: number; radius: number },
  enemies: readonly RaidUnit[],
  allyAnchor: { x: number; y: number } | null,
  fleeDistOrOpts?: number | SpaceFromThreatOpts,
): { tx: number; ty: number } {
  const t = threatTable();
  const opts: SpaceFromThreatOpts =
    typeof fleeDistOrOpts === "number"
      ? { fleeDist: fleeDistOrOpts }
      : (fleeDistOrOpts ?? {});
  const role = opts.role;
  const dist = opts.fleeDist ?? raidPackFleeDist(role);
  const allyPull =
    opts.allyPull != null
      ? Math.min(1, Math.max(0, opts.allyPull))
      : 0;
  const nearestBiasRaw = Number(t.pack?.nearestBias);
  const nearestBias = Number.isFinite(nearestBiasRaw)
    ? Math.min(1, Math.max(0, nearestBiasRaw))
    : 0.45;
  const standoffPad =
    raidPackStandoffPad(role) + Math.max(0, opts.aaClearPad ?? 0);

  let sx = 0;
  let sy = 0;
  let sw = 0;
  let n = 0;
  let nearest: RaidUnit | null = null;
  let nearestD = Infinity;
  const r = raidPackRadius();
  for (const e of enemies) {
    if (e.team !== 1 || e.hp <= 0 || !e.combatArmed) continue;
    const d = Math.hypot(e.x - u.x, e.y - u.y);
    if (d > r + e.radius) continue;
    const w = hostileWeight(e, t);
    sx += e.x * w;
    sy += e.y * w;
    sw += w;
    n += 1;
    if (d < nearestD) {
      nearestD = d;
      nearest = e;
    }
  }
  if (n <= 0) {
    return { tx: u.x, ty: u.y };
  }
  const cx = sx / sw;
  const cy = sy / sw;
  let dx = u.x - cx;
  let dy = u.y - cy;
  let len = Math.hypot(dx, dy);
  if (len < 1e-3) {
    dx = 0;
    dy = 1;
    len = 1;
  }
  dx /= len;
  dy /= len;

  if (nearest && nearestBias > 0) {
    let ndx = u.x - nearest.x;
    let ndy = u.y - nearest.y;
    let nlen = Math.hypot(ndx, ndy);
    if (nlen < 1e-3) {
      ndx = dx;
      ndy = dy;
      nlen = 1;
    } else {
      ndx /= nlen;
      ndy /= nlen;
    }
    dx = dx * (1 - nearestBias) + ndx * nearestBias;
    dy = dy * (1 - nearestBias) + ndy * nearestBias;
    len = Math.hypot(dx, dy) || 1;
    dx /= len;
    dy /= len;
  }

  let tx = cx + dx * dist;
  let ty = cy + dy * dist;

  if (allyAnchor && allyPull > 0) {
    tx += (allyAnchor.x - tx) * allyPull;
    ty += (allyAnchor.y - ty) * allyPull;
  }

  if (nearest) {
    // Standoff past Soft ∪ enemy AA so ranged space goals are not plantable
    // inside thrall/brute hit reach (Soft disk ≪ AA for adds).
    const minStand =
      Math.max(
        enemyMeleeDiskRadius(nearest, u.radius, t),
        enemyHitReach(nearest, u.radius),
      ) + standoffPad;
    const dGoal = Math.hypot(tx - nearest.x, ty - nearest.y);
    if (dGoal < minStand) {
      let ndx = tx - nearest.x;
      let ndy = ty - nearest.y;
      let nlen = Math.hypot(ndx, ndy);
      if (nlen < 1e-3) {
        ndx = dx;
        ndy = dy;
        nlen = 1;
      }
      tx = nearest.x + (ndx / nlen) * minStand;
      ty = nearest.y + (ndy / nlen) * minStand;
    }
  }

  if (role === "tank") {
    // Soft≪AA: Role tankMaxStepPx (52) crawls out of thrall packs — seed17/3
    // tanks stayed engage aa3→7 until melt. Skip cap while feet in thrall AA.
    const thrallChew = !clearOfThrallAttack(u.x, u.y, u.radius, enemies, 2);
    const maxStep = thrallChew ? null : raidPackTankMaxStepPx();
    if (maxStep != null) {
      const sdx = tx - u.x;
      const sdy = ty - u.y;
      const sLen = Math.hypot(sdx, sdy);
      if (sLen > maxStep) {
        tx = u.x + (sdx / sLen) * maxStep;
        ty = u.y + (sdy / sLen) * maxStep;
      }
    }
  }

  return { tx, ty };
}

export function raidThreatSegmentSteps(): number {
  return threatTable().segmentSteps ?? 10;
}

export function raidThreatMaxAcceptCost(): number {
  return threatTable().maxAcceptCost ?? 36;
}

/**
 * Ranged kite stand: primary band above **enemy hit reach** floor (measure
 * standMul only widens above that), plus secondary keep-out
 * (Soft melee ∪ weapon-band ∪ enemy AA). Clamped into hero weapon reach.
 */
export function kiteStandWithSecondaryThreats(
  u: Pick<RaidUnit, "x" | "y" | "radius" | "attackRange">,
  primary: { x: number; y: number; id?: number },
  enemies: readonly RaidUnit[],
  opts: {
    standMul: number;
    maxPrimaryDist: number;
    role?: "tank" | "dps" | "healer";
    /** Extra AA margin from 자기보호; pass 0 when charge corridor active. */
    aaClearPad?: number;
  },
): { tx: number; ty: number } {
  const t = threatTable();
  const standMul = Math.max(0, opts.standMul);
  const maxD = Math.max(1, opts.maxPrimaryDist);
  const primaryEnemy =
    primary.id != null
      ? enemies.find((e) => e.id === primary.id && e.hp > 0)
      : null;
  const hitFloor =
    primaryEnemy != null
      ? enemyHitReach(primaryEnemy, u.radius) + 2
      : 0;
  // Preferred ∈ [enemyHitReach+eps, heroAttackReach]; low measure hugs the floor.
  const stand = Math.min(maxD, Math.max(u.attackRange * standMul, hitFloor));
  const curPrimary = Math.hypot(u.x - primary.x, u.y - primary.y);
  const primaryInAa = curPrimary <= maxD;
  const clearOfPrimaryHit =
    hitFloor <= 0 || curPrimary > hitFloor - 1e-3;
  const standHoldEps = Math.max(8, u.attackRange * 0.06);
  const standoffPad =
    raidPackStandoffPad(opts.role) * Math.max(0.05, standMul) +
    Math.max(0, opts.aaClearPad ?? 0);
  const considerR = Math.max(raidPackRadius(), u.attackRange, opts.maxPrimaryDist);

  /** Soft melee ∪ weapon-band×standMul ∪ enemy hit reach. */
  const secondaryMinStand = (
    e: Pick<RaidUnit, "radius" | "defId" | "attackRange">,
  ): number => {
    const meleeStand = enemyMeleeDiskRadius(e, u.radius, t) + standoffPad;
    const weaponBand =
      (u.attackRange + e.radius) * Math.max(0.05, standMul);
    const hitKeep = enemyHitReach(e, u.radius);
    return Math.max(meleeStand, weaponBand, hitKeep);
  };

  let crowded = false;
  for (const e of enemies) {
    if (e.team !== 1 || e.hp <= 0 || !e.combatArmed) continue;
    if (primary.id != null && e.id === primary.id) continue;
    const dUnit = Math.hypot(u.x - e.x, u.y - e.y);
    if (dUnit > considerR + e.radius) continue;
    if (dUnit < secondaryMinStand(e)) {
      crowded = true;
      break;
    }
  }

  let tx: number;
  let ty: number;
  // Plant only when outside enemy AA floor and on the stand band.
  if (
    primaryInAa &&
    clearOfPrimaryHit &&
    Math.abs(curPrimary - stand) <= standHoldEps &&
    !crowded
  ) {
    tx = u.x;
    ty = u.y;
  } else {
    const pdx0 = u.x - primary.x;
    const pdy0 = u.y - primary.y;
    const plen0 = Math.hypot(pdx0, pdy0) || 1;
    tx = primary.x + (pdx0 / plen0) * stand;
    ty = primary.y + (pdy0 / plen0) * stand;
  }

  for (const e of enemies) {
    if (e.team !== 1 || e.hp <= 0 || !e.combatArmed) continue;
    if (primary.id != null && e.id === primary.id) continue;
    const dUnit = Math.hypot(u.x - e.x, u.y - e.y);
    const minStand = secondaryMinStand(e);
    const dGoal = Math.hypot(tx - e.x, ty - e.y);
    if (dUnit > considerR + e.radius && dGoal >= minStand) continue;
    if (dGoal >= minStand) continue;
    let ndx = tx - e.x;
    let ndy = ty - e.y;
    let nlen = Math.hypot(ndx, ndy);
    if (nlen < 1e-3) {
      ndx = tx - primary.x;
      ndy = ty - primary.y;
      nlen = Math.hypot(ndx, ndy) || 1;
    }
    tx = e.x + (ndx / nlen) * minStand;
    ty = e.y + (ndy / nlen) * minStand;
  }

  const pdx = tx - primary.x;
  const pdy = ty - primary.y;
  const plen = Math.hypot(pdx, pdy) || 1;
  if (plen > maxD) {
    tx = primary.x + (pdx / plen) * maxD;
    ty = primary.y + (pdy / plen) * maxD;
  } else if (hitFloor > 0 && plen < hitFloor && hitFloor <= maxD) {
    tx = primary.x + (pdx / plen) * hitFloor;
    ty = primary.y + (pdy / plen) * hitFloor;
  }
  return { tx, ty };
}

/** LHF alias — same geometry as {@link spaceFromThreatGoal}. */
export const spaceFromLocalHostileGoal = spaceFromThreatGoal;

/** LHF alias — same geometry as {@link kiteStandWithSecondaryThreats}. */
export const kiteStandOnLocalHostile = kiteStandWithSecondaryThreats;

export function raidThreatTangentialOffsets(): readonly number[] {
  return threatTable().tangentialOffsets ?? [70, -70, 110, -110];
}

/** Local threat below this → survive score 0 (engage/kite). Not boss distance. */
export function raidSurviveThreatSafe(): number {
  return Number(threatTable().surviveThreatSafe) || 8;
}

/**
 * Soft intensity above this → ranged engage prefers spaceFromThreat.
 * Below single thrall melee weight (14) so one armed add in disk opens space;
 * brute (20) always does.
 */
export function raidEngageSoftSpace(): number {
  const v = Number(threatTable().engageSoftSpace);
  return Number.isFinite(v) ? v : 10;
}

/**
 * Soft intensity above this → measure plant forbidden (keep moving to stand/space).
 * Defaults to surviveThreatSafe when unset.
 */
export function raidSoftPlantMax(): number {
  const v = Number(threatTable().softPlantMax);
  if (Number.isFinite(v)) return v;
  return raidSurviveThreatSafe();
}

/**
 * Extra clearance beyond enemy hit reach (and Soft melee) before SoftThreat
 * space latch releases. Soft volumes are binary — without pad, soft-sep on the
 * Soft lip flips space↔kite; exit must still clear **enemy AA**, not Soft disk only.
 */
export function raidSoftSpaceExitPad(): number {
  const v = Number(threatTable().softSpaceExitPad);
  return Number.isFinite(v) && v >= 0 ? v : 12;
}

/** @deprecated Prefer {@link nearEnemyHitOrMelee} — Soft disk alone understates thrall AA. */
export function nearEnemyMeleeDisk(
  x: number,
  y: number,
  unitRadius: number,
  enemies: readonly RaidUnit[],
  pad = 0,
): boolean {
  return nearEnemyHitOrMelee(x, y, unitRadius, enemies, pad);
}

/**
 * SoftThreat → spaceFromThreat with exit hysteresis.
 * Enter when intensity above thr **or** feet are inside Soft∪enemy-AA
 * (thrall AA ≫ Soft disk — sticky adds must open space without waiting for
 * Soft-lip intensity). Hold until clear of max(Soft melee, enemy AA) + pad
 * (+ 자기보호 aaClear when no charge corridor).
 */
export function softThreatWantsSpace(args: {
  u: RaidUnit;
  softIntensity: number;
  enterThr: number;
  enemies: readonly RaidUnit[];
  dangerZones?: readonly RaidDangerZone[];
}): boolean {
  const intensityEnter =
    Number.isFinite(args.enterThr) && args.softIntensity > args.enterThr;
  const aaEnter = nearEnemyHitOrMelee(
    args.u.x,
    args.u.y,
    args.u.radius,
    args.enemies,
    0,
  );
  if (intensityEnter || aaEnter) {
    args.u.brain.softSpaceLatch = true;
    return true;
  }
  if (!args.u.brain.softSpaceLatch) return false;
  const protectPad =
    args.dangerZones != null
      ? effectiveAaClearPadPx(args.u, args.dangerZones)
      : aaClearPadPxOf(args.u);
  if (
    nearEnemyHitOrMelee(
      args.u.x,
      args.u.y,
      args.u.radius,
      args.enemies,
      raidSoftSpaceExitPad() + protectPad,
    )
  ) {
    return true;
  }
  args.u.brain.softSpaceLatch = false;
  return false;
}

/** playBounds edge proximity under this → retreat goes straight to ally anchor. */
export function raidSurviveLipMargin(): number {
  return Number(threatTable().surviveLipMargin) || 24;
}
